import React, { forwardRef, memo, useEffect, useImperativeHandle, useState } from 'react';
import { FormControl } from '@material-ui/core';
import { Typography } from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { FieldHelperOrErrors } from './FieldHelperOrErrors';
import { useStyles } from './Field.styles';
import { DateFieldProps, FormFieldRef } from './Field.props';
import { shallowCompare } from './shallowCompare';
import { TypedFormFieldValue } from '../Form/Form.types';
import ClearIcon from '@material-ui/icons/Clear';
import moment, { Moment } from 'moment';

export const DateField = memo(
  forwardRef<FormFieldRef<Date>, DateFieldProps>((props, ref) => {
    const classes = useStyles();
    const { isDisabled, label, helperText, errorTexts, onChange, required } = props;
    const [value, setValue] = useState<Date | null>(
      (props.initialState as TypedFormFieldValue<Date> | undefined)?.value ?? null,
    );

    useImperativeHandle(ref, () => ({
      setValue: (value: TypedFormFieldValue<Date> | TypedFormFieldValue<Date>[] | undefined) => {
        if (!Array.isArray(value)) {
          setValue(value?.value ?? null);
        }
      },
    }));

    useEffect(() => {
      if (onChange) {
        if (value === null) {
          onChange(undefined);
        } else {
          onChange({
            value: value,
            isValid: required && value === null ? false : true,
          });
        }
      }
    }, [value, onChange, required]);

    const handleChange = (date: Moment | null) => {
      let newDate = date ? moment.utc({ year: date.year(), month: date.month(), day: date.date() }).toDate() : null;
      // let x = date?.toDate();
      // let newDate = x? new Date(Date.UTC(x.getFullYear(),x.getMonth(),x.getDay()+1)) : null;
      setValue(newDate);
    };

    const [isOpen, setIsOpen] = useState<boolean>(false);

    return (
      <FormControl error={props.error} className={classes.formControl}>
        <Typography className={classes.label} component="label">
          {label}
          {props.required&&label && <span aria-hidden="true">*</span>}
        </Typography>
        <KeyboardDatePicker
          autoOk
          variant="inline"
          keyboardIcon={(value && !required && <ClearIcon />) || undefined}
          renderDay={props.onRenderDay}
          onOpen={() => {
            if (value && !required) {
              setValue(null);
              setIsOpen(false);
            } else {
              setIsOpen(true);
            }
          }}
          onClose={() => {
            setIsOpen(false);
          }}
          open={isOpen}
          InputProps={{
            required: props.required,
            disableUnderline: true,
            classes: { focused: classes.focused, error: classes.error },
          }}
          disabled={isDisabled}
          helperText={''}
          value={value}
          placeholder={props.placeholder}
          onChange={handleChange}
          format="YYYY-MM-DD"
        />

        <FieldHelperOrErrors helperText={helperText} errorTexts={errorTexts} />
      </FormControl>
    );
  }),
  (newProps, oldProps) => shallowCompare(newProps, oldProps, ['errorTexts']),
);

export default DateField;
